import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import useProfileQuestions from 'services/general/profileQuestions';
import useSampleInfoQuestions from 'services/surveys/useSampleInfoQuestions';
import useUpdateProfileAnswer from 'services/surveys/useUpdateProfileAnswer';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import { useUrlQuery } from 'hooks/useUrlQuery';
import {
  ProfileQuestion,
  ProfileQuestionAnswer,
  ProfileQuestionsGroup,
} from 'types/ProfileQuestions';

import {
  ButtonColors,
  ButtonSizes,
  ContainerSizes,
  DesignButton,
  Header,
  PageContainer,
} from 'components';
import { ProfileConfirmationGroup } from './ProfileConfirmationGroup';

import styles from './ProfileConfirmation.module.scss';

export type ProfileFormData = {
  [key: string]: number | string | string[];
};
export type ProfileActionVariant = 'view' | 'edit' | 'confirm';
export type ProfileFlowType = 'sampleinfo' | 'profile';

const ProfileConfirmation = ({
  variant,
}: {
  variant?: ProfileActionVariant;
}) => {
  const flowType = useUrlQuery().get('flow');
  const { currentKitId, currentTinyAccountId } = useCurrentKitAndTinyAccount();

  if (currentKitId && currentTinyAccountId) {
    return flowType === 'sampleinfo' ? (
      <SampleInfoFlow
        tinyAccountId={currentTinyAccountId}
        kitId={currentKitId}
        variant={variant}
      />
    ) : (
      <ProfileFlow
        tinyAccountId={currentTinyAccountId}
        kitId={currentKitId}
        variant={variant}
      />
    );
  }
  return <></>;
};

const ProfileFlow = ({
  tinyAccountId,
  kitId,
  variant,
}: {
  tinyAccountId: string;
  kitId: string;
  variant?: ProfileActionVariant;
}) => {
  const { data: profileQuestions } = useProfileQuestions(tinyAccountId, kitId);
  const profileAnswerMutation = useUpdateProfileAnswer();

  const mutationCallback = useCallback(
    (answers: ProfileQuestionAnswer[]) => {
      return profileAnswerMutation.mutateAsync({
        answers,
        tinyAccountId,
        kitId,
      });
    },
    [profileAnswerMutation],
  );

  if (!tinyAccountId) return <></>;
  return (
    <THSurveyConfirmation
      questionGroups={profileQuestions}
      mutationCallback={mutationCallback}
      kitId={kitId}
      variant={variant}
      flow='profile'
    />
  );
};

const SampleInfoFlow = ({
  tinyAccountId,
  kitId,
  variant,
}: {
  tinyAccountId: string;
  kitId: string;
  variant?: ProfileActionVariant;
}) => {
  const { data: sampleInfoQuestions } = useSampleInfoQuestions(
    tinyAccountId,
    kitId,
  );
  const profileAnswerMutation = useUpdateProfileAnswer();

  const mutationCallback = useCallback(
    (answers: ProfileQuestionAnswer[]) => {
      return profileAnswerMutation.mutateAsync({
        answers,
        tinyAccountId,
        kitId,
      });
    },
    [profileAnswerMutation],
  );

  if (!tinyAccountId) return <></>;
  return (
    <THSurveyConfirmation
      questionGroups={sampleInfoQuestions}
      mutationCallback={mutationCallback}
      kitId={kitId}
      variant={variant}
      flow='sampleinfo'
    />
  );
};

const THSurveyConfirmation = ({
  questionGroups,
  mutationCallback,
  kitId,
  variant,
  flow,
}: {
  questionGroups?: ProfileQuestionsGroup[] | null;
  mutationCallback?: (answers: ProfileQuestionAnswer[]) => Promise<void>;
  kitId: string;
  variant?: ProfileActionVariant;
  flow: ProfileFlowType;
}) => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async () => {
    setIsSubmitting(true);
    const allQuestions =
      questionGroups?.reduce<ProfileQuestion[]>(
        (
          accumulator: ProfileQuestion[],
          currentGroup: ProfileQuestionsGroup,
        ) => {
          return accumulator.concat(currentGroup.questions);
        },
        [],
      ) ?? [];
    const answers = allQuestions?.map(question => {
      return { question_id: question.id, answer_text: question.answer_text };
    });
    if (mutationCallback) {
      try {
        await mutationCallback(answers);
        navigate(state?.from ? state.from : `/`);
        toast.success('Answers saved successfully');
      } catch (error) {
        toast.error('Something went wrong, please try again');
      }
    }
    setIsSubmitting(false);
  };

  const headerTitle =
    variant === 'view'
      ? 'View your answers'
      : variant === 'confirm'
        ? 'Review your previous answers'
        : 'Edit your answers';

  if (!questionGroups?.length) return <></>;
  return (
    <>
      <Header title={headerTitle} />
      <PageContainer
        size={ContainerSizes.SM}
        className={styles.profileContainer}
      >
        <div className={styles.groupsContainer}>
          {questionGroups.map(questionGroup => (
            <ProfileConfirmationGroup
              key={questionGroup.id}
              questionGroup={questionGroup}
              variant={variant}
              flow={flow}
            />
          ))}
          <div className={styles.buttons}>
            {variant === 'confirm' && (
              <DesignButton
                label='Continue'
                loading={isSubmitting}
                size={ButtonSizes.M}
                color={ButtonColors.PURPLE}
                width={'full'}
                className='mt-2'
                onClick={onSubmit}
              />
            )}
          </div>
        </div>
      </PageContainer>
    </>
  );
};

export default ProfileConfirmation;
